import { useMemo } from "react";
import { Skeleton } from "primereact/skeleton";
import { Button } from "primereact/button";

import { useUserExams } from "sj-ui-toolkit/src/api/userInitiatedExams";
import { useSmartNavigation } from "sj-ui-toolkit/src/helpers/navigation";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts/User/UserContextProvider";
import { buildRoute, UserAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { categoryEstimationConfig } from "sj-ui-toolkit/src/atlas/utils/kaConstants";
import { useSettingsContext } from "sj-ui-toolkit/src/atlas/contexts/Settings/SettingsContextProvider";
import { FEATURES } from "sj-ui-toolkit/src/constants/features";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";

import { AssessmentCard } from "../Dashboard/components/DashboardCards/AssessmentCard";
import { SectionDetail } from "./SectionDetail";
import S from "./Assessments.module.scss";

// Main assessments page component, showing cards for untaken or in-progress sections and detailed results for taken sections
const Assessments = () => {
  useBreadcrumb({ label: "Assessments", route: "/assess" });
  const { navigateTo } = useSmartNavigation();
  const { selectedTenant } = useUserContext();
  const { data, isLoading, error } = useUserExams({ enabled: !!selectedTenant });
  const { isFeatureEnabled } = useSettingsContext();
  const userInitiatedExams = isFeatureEnabled(FEATURES.SELF_ASSESSMENTS);

  const { untaken, taken } = useMemo(() => {
    if (!data) return { untaken: [], taken: [] };
    return {
      untaken: data.sections.filter(s => s.latestScorePercent == null || s.inProgressExam),
      taken: data.sections.filter(s => s.latestScorePercent != null),
    };
  }, [data]);

  const navigateToExamSection = sectionPath => {
    navigateTo(buildRoute("Ka.routes.userInitiatedExam.route", { section: sectionPath }));
  };

  if (isLoading || !data) {
    return (
      <div data-test-id="learn-assessments-page">
        <h1 className="mt-4 atlas-h2">Assessments</h1>
        <div role="status" aria-live="polite" aria-busy="true">
          <span className="sr-only">Loading assessments…</span>
          <Skeleton width="100%" height="300px" className="mt-4" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div data-test-id="learn-assessments-page">
        <h1 className="mt-4 atlas-h2">Assessments</h1>
        <p className="body-md mt-4 error-text" role="alert">
          Failed to load assessments. Please try again.
        </p>
      </div>
    );
  }

  return (
    <div data-test-id="learn-assessments-page" className="pb-20">
      <h1 className="mt-4 atlas-h2">Assessments</h1>

      {!userInitiatedExams && (
        <p className="body-md mt-2 mb-8 w-2xl">
          Interested in assessing your proficiency with secure development concepts and practices?
          Ask your admin to assign you an assessment!
        </p>
      )}

      {/* Cards for sections not yet taken (or in-progress) */}
      {userInitiatedExams && untaken.length > 0 && (
        <section className="mt-4" aria-labelledby="untaken-heading">
          <h2 id="untaken-heading" className="atlas-h5 mb-4">
            Assess your knowledge and progress
          </h2>
          <ul className={S.cardsRow} aria-label="Available assessments">
            {untaken.map(({ section, inProgressExam }) => {
              const sectionPath = section.path ?? "";
              const estimatedTime = categoryEstimationConfig[sectionPath]?.estFinishTime;

              return (
                <li key={sectionPath}>
                  <AssessmentCard
                    size="small"
                    type="Assessment"
                    title={section.title}
                    description={section.description}
                    estimatedTime={estimatedTime}
                    status={inProgressExam ? "InProgress" : undefined}
                    onNavigate={() => navigateToExamSection(sectionPath)}
                  />
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* Add a header if there aren't any untaken assessments */}
      {userInitiatedExams && untaken.length === 0 && (
        <section className="mt-4" aria-labelledby="completed-heading">
          <h2 id="completed-heading" className="atlas-h3 mb-4">
            All assessments completed
          </h2>
          <p className="body-md mt-2 w-2xl">You have completed all assessments. Great work!</p>
          <p className="body-md mt-2 w-2xl">
            We recommend retaking assessments periodically to track your progress and get updated
            recommendations.
          </p>
        </section>
      )}

      {/* Detailed results for sections already taken */}
      {taken.length > 0 && (
        <>
          <div className="relative">
            <Button
              link
              icon="pi pi-arrow-right"
              iconPos="right"
              label="View all assessment results"
              onClick={() => navigateTo(UserAppRoutes.routes.history.route)}
              className="absolute right-0 top-2"
            />
          </div>
          <section aria-labelledby="results-heading">
            <h2 id="results-heading" className="sr-only">
              Your assessment results
            </h2>
            {taken.map(sectionData => (
              <SectionDetail
                key={sectionData.section.path}
                sectionData={sectionData}
                onRetake={navigateToExamSection}
              />
            ))}
          </section>
        </>
      )}
    </div>
  );
};

export default Assessments;
